import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/spaces/contact-request/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type SpaceContactRequestInput = paths[url]['post']['requestBody']['content']['application/json'];

export interface SpaceContactRequestSuccessResponse {
    data: paths[url]['post']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to send contact request for a space.
 */
export const useSpaceContactRequest = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async (uuid: string, props: SpaceContactRequestInput) => {
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const response = await fetch(`${API_URL}/spaces/contact-request/${uuid}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(props),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            setSuccess(true);
        } catch (err: any) {
            handleCommonErrors(err);
            err.message && setError(err.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, success, error, lastResponseTime };
};
